import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { isQueryReady } from "src/api/axiosClient/utils";
import { Variations } from "src/context/OlapScores/useVariations";
import { MainScore, NumericalScoreValue } from "../../olapScores/olapScoresSchema";

export const getOrganizationOverviewSsqKey = "getOneCompanySsqKey" as const;
type Options = {
    companyId: number | string | undefined;
    campaignId: number | string | undefined;
    ssqId: number | string | undefined;
    variations?: Variations;
};

export const getOrganizationOverviewOneSsq = async (options: Options) => {
    const { companyId, campaignId, ssqId, variations = {} } = options;
    const url = `/api/organizations/${companyId}/overview/ssq/${ssqId}/campaign/${campaignId}`;

    const response = await loggedInApiCall.get(url, {
        params: {
            scores: {
                variations,
                dimensions: [
                    [["headings"], ["subheadings"], ["properties"], ["subproperties"], ["multivalues"]],
                    [
                        ["ssq_versions"],
                        ["headings"],
                        ["subheadings"],
                        ["properties"],
                        ["subproperties"],
                        ["multivalues"],
                    ],
                ],
            },
        },
    });

    return response.data as CompanyOverviewOneSsq;
};

export const useOrganizationOverviewOneSsq = (options: Options) => {
    const { companyId, campaignId, ssqId, variations = {} } = options;

    const isEnabled = companyId !== undefined && campaignId !== undefined && ssqId !== undefined;
    const query = useQuery({
        queryKey: [getOrganizationOverviewSsqKey, companyId, campaignId, ssqId, variations],
        cacheTime: 0,
        enabled: isEnabled,
        queryFn: async () => {
            return getOrganizationOverviewOneSsq({ companyId, campaignId, ssqId, variations });
        },
    });

    const isReady = isQueryReady(query, isEnabled);

    return {
        overview: isReady ? query.data : undefined,
        isReady,
        ...query,
    };
};

// I didn't do the Zod schema because there is a complex recursive structure with dimensions that is not already typed
// If needed, we can do it later

export type RegulatoryScore =
    | {
          required: true;
          title: string;
          description: string;
          tags: string[];
          imageUrl?: string;
          url?: string;
      }
    | { required: false };
export type RegulatoryScoreMapping = Record<string, RegulatoryScore>;

export type CompanyOverviewOneSsq = {
    ssqId: number;
    ssqTitle: string;
    completion: number | null;
    isLocked: boolean | null;
    scoresOverview: {
        "hydra:member": Array<CompanyOverviewOneScore>;
    };
};

export type CompanyOverviewOneScore = {
    actionPlan: ActionPlanOverview | null | false;
    score: ScoreOverview;
    ssq: QuestionnaireOverview | null;
    target: TargetOverview | null | false;
};

export type TargetOverview = {
    label: string;
    value: NumericalScoreValue | undefined;
    description: string;
};

export type ScoreOverview = {
    id: number;
    title: string;
    unit: string;
    displayMode: string;
    type: string;
    children: Array<ScoreOverview>;
};

export type QuestionnaireOverview = {
    id: number;
    title: string;
    post: {
        id: number;
        ssqVersionId: number;
        completion?: number;
        isLocked?: boolean;
        canViewSsq: boolean;
        canAnswerSsq: boolean;
        canValidateSsq: boolean;
        prevCampaign?: {
            value: NumericalScoreValue;
            year: string;
        } | null;
        scores?: MainScore;
    } | null;
    scores?: {
        id: number;
    }[];
};

export type ActionPlanOverview = {
    name: string;
    campaigns: ActionPlanCampaigns;
    completion: {
        engaged: ActionPlanSummaryType;
    };
};

export type ActionPlanCampaignValue = { year: string; value: number | null };
export type ActionPlanCampaigns = {
    start: ActionPlanCampaignValue;
    current: ActionPlanCampaignValue;
    end: ActionPlanCampaignValue;
};

export type ActionPlanSummaryType = {
    total: number;
    byType: {
        completed: number;
        considered: number;
        integrated: number;
        ongoing: number;
        planned: number;
    };
};
